/**
 * Application configuration
 */

// API base URL - use environment variable with fallback
const RAW_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
if (!RAW_API_BASE_URL) {
  console.error('VITE_API_BASE_URL environment variable is not set');
}

/**
 * Normalize VITE_API_BASE_URL for callers that append `/api/...` paths.
 * Strips trailing slashes and a trailing `/api` segment so
 * `https://host/api` + `/api/auth/...` does not become `/api/api/...`.
 */
export const normalizeApiBaseUrl = (url) => {
  if (!url) return '';
  let normalized = url.replace(/\/+$/, '');
  if (normalized.endsWith('/api')) {
    normalized = normalized.slice(0, -4).replace(/\/+$/, '');
  }
  return normalized;
};

const cleanApiUrl = normalizeApiBaseUrl(RAW_API_BASE_URL);
const API_BASE_URL = cleanApiUrl;

// Keycloak configuration
const KEYCLOAK_CONFIG = {
  url: import.meta.env.VITE_KEYCLOAK_URL?.replace(/\/+$/, '') ,
  realm: import.meta.env.VITE_KEYCLOAK_REALM ,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
};

const isDevelopment = import.meta.env.MODE === 'development';
export const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true' || isDevelopment;
export const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';

export const DEBUG = {
  logApiCalls: isDevelopment || DEV_MODE,  // Only log API calls in development
  logAuthEvents: isDevelopment || DEV_MODE // Only log authentication events in development
};

// Feature flags
export const FEATURES = {
  enableShareFeature: false,
  enableExportFeature: true,
  enableAdvancedSearch: false,
};

// Session timeout in seconds (5 days)
export const SESSION_TIMEOUT = 432000;

export { KEYCLOAK_CONFIG, API_BASE_URL };

// Secure logging utility that filters sensitive data
export const secureLog = {
  // Safe info logging - filters out sensitive data
  info: (...args) => {
    if (DEBUG.logApiCalls) {
      const safeArgs = args.map(arg => filterSensitiveData(arg));
      console.log(...safeArgs);
    }
  },
  
  // Safe warn logging - filters out sensitive data  
  warn: (...args) => {
    if (DEBUG.logApiCalls) {
      const safeArgs = args.map(arg => filterSensitiveData(arg));
      console.warn(...safeArgs);
    }
  },
  
  // Safe error logging - filters out sensitive data
  error: (...args) => {
    if (DEBUG.logApiCalls) {
      const safeArgs = args.map(arg => filterSensitiveData(arg));
      console.error(...safeArgs);
    }
  },
  
  // Auth-specific logging that's extra careful with tokens
  auth: (...args) => {
    if (DEBUG.logAuthEvents) {
      const safeArgs = args.map(arg => filterSensitiveData(arg));
      console.log('[AUTH]', ...safeArgs);
    }
  }
};

// Helper function to filter sensitive data from logs
const filterSensitiveData = (data) => {
  if (typeof data === 'string') {
    // Replace any JWT tokens (base64 strings that look like tokens)
    return data.replace(/eyJ[A-Za-z0-9+/=]+\.eyJ[A-Za-z0-9+/=]+\.[A-Za-z0-9+/=_-]+/g, '[TOKEN_HIDDEN]');
  }
  
  if (typeof data === 'object' && data !== null) {
    const filtered = Array.isArray(data) ? [] : {};
    
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        // Hide sensitive fields
        if (key.toLowerCase().includes('token') || 
            key.toLowerCase().includes('password') || 
            key.toLowerCase().includes('secret') ||
            key.toLowerCase().includes('authorization')) {
          filtered[key] = '[HIDDEN]';
        } else {
          filtered[key] = filterSensitiveData(data[key]);
        }
      }
    }
    
    return filtered;
  }
  
  return data;
};

// Helper function to construct API URLs
export const getApiUrl = (endpoint) => {
    // Check if endpoint is already a fully-qualified URL
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
        // Parse the URL to extract just the path
        try {
            const url = new URL(endpoint);
            endpoint = url.pathname + url.search + url.hash;
        } catch (e) {
            secureLog.warn('Invalid URL detected, using as path:', endpoint);
        }
    }
    
    // Remove leading and trailing slashes from endpoint
    const cleanEndpoint = endpoint.replace(/^\/+|\/+$/g, '');
    
    // Use the configured API base URL
    return `${cleanApiUrl}/${cleanEndpoint}`;
};

export const getApiBaseUrl = () => {
    if (!RAW_API_BASE_URL) {
        throw new Error('API_BASE_URL is not configured. Please set VITE_API_BASE_URL environment variable.');
    }
    return cleanApiUrl;
};

